// victoria-client/src/components/timeseries-chart/TimeseriesCanvas.tsx
import "chartjs-adapter-dayjs-3";

import { useMemo, type ReactNode } from "react";

import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import {
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";

import { todayLinePlugin } from "./plugins/todayLine";
import type { Point, ViewRange } from "./types";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  TimeScale,
  zoomPlugin,
  todayLinePlugin,
);

type Options = ChartOptions<"line"> & {
  plugins: NonNullable<ChartOptions<"line">["plugins"]> & {
    todayLine?: { value: string; label: string };
  };
};

// Stable default so an omitted `pluginOptions` prop doesn't change identity
// between renders (the future stock caller won't pass it). A fresh `{}` literal
// would re-trigger the `options` useMemo on every render.
const EMPTY_PLUGIN_OPTIONS: Record<string, object> = {};

export type TimeseriesCanvasProps = {
  /** Pre-built Chart.js datasets — caller owns colors, labels, y values. */
  data: ChartData<"line", Point[]>;
  /** Total data span (used to clamp pan/zoom). */
  dataSpan: ViewRange;
  /** Currently-visible window (epoch ms). */
  viewRange: ViewRange;
  /** y-axis title. */
  yAxisLabel: string;
  /** Minimum visible x-range in ms (zoom-in cap). */
  minRangeMs: number;
  /** ISO date for the "today" marker. When omitted, no marker is drawn. */
  todayValue?: string;
  /** Optional y tick formatter (e.g. € for revenue). */
  formatYTick?: (value: number) => string;
  /** Loading state. */
  isLoading: boolean;
  /** Skeleton shown while loading. */
  skeleton: ReactNode;
  /** Called when the user pans/zooms. */
  onUserRange: (range: ViewRange) => void;
  /**
   * Enable horizontal drag-to-pan. Off by default so it never hijacks a
   * per-instance left-drag interaction — notably demand's range-select (which
   * draws the totals popup). Charts without such a drag (e.g. stock) opt in.
   */
  enablePan?: boolean;
  /** Per-instance Chart.js plugins (e.g. demand's rangeSelect). */
  plugins?: Plugin<"line">[];
  /**
   * Additional plugin option blocks merged into `options.plugins` (e.g. the
   * `rangeSelect` options consumed by demand's per-instance plugin). These are
   * spread *after* the core's built-in blocks, so callers must not reuse the
   * core's reserved keys (`zoom`, `tooltip`, `legend`, `todayLine`) — doing so
   * would silently override the core's own configuration.
   */
  pluginOptions?: Record<string, object>;
  /** Overlay rendered inside the relative wrapper (e.g. a totals popup). */
  children?: ReactNode;
  /** Height of the chart area in px. Defaults to 420. */
  height?: number;
};

export function TimeseriesCanvas({
  data,
  dataSpan,
  viewRange,
  yAxisLabel,
  minRangeMs,
  todayValue,
  formatYTick,
  isLoading,
  skeleton,
  onUserRange,
  enablePan = false,
  plugins = [],
  pluginOptions = EMPTY_PLUGIN_OPTIONS,
  children,
  height = 420,
}: TimeseriesCanvasProps) {
  const { t } = useTranslation();
  const options = useMemo<Options>(() => {
    const pushIfChanged = (c: ChartJS) => {
      const { min, max } = c.scales.x;
      if (
        Math.abs(min - viewRange.min) < 1000 &&
        Math.abs(max - viewRange.max) < 1000
      )
        return;
      onUserRange({ min, max });
    };
    return {
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      // Group tooltip rows by nearest *x-value*, not by array index. These
      // charts stack history and forecast/projection series in one datasets
      // array with different x's and lengths, so `mode: "index"` reads
      // dataset.data[i] from every series — pulling a wrong-dated history point
      // into the forecast region (wrong title date + phantom rows). Nearest-by-x
      // returns only the series co-located at the hovered date (e.g. forecast
      // mean + band together) and the real date.
      interaction: { mode: "nearest", axis: "x", intersect: false },
      // Room above the chart area for the today line's label.
      layout: { padding: { top: todayValue ? 16 : 0 } },
      scales: {
        x: {
          type: "time",
          time: { unit: "month" },
          min: viewRange.min,
          max: viewRange.max,
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: yAxisLabel },
          ...(formatYTick
            ? {
                ticks: {
                  callback: (v: number | string) => formatYTick(Number(v)),
                },
              }
            : {}),
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          position: "nearest",
          backgroundColor: "rgba(255,255,255,0.9)",
          borderWidth: 1,
          borderColor: "#bbbbbb",
          bodyColor: "#000",
          titleColor: "#000",
          padding: 14,
          // Unlabelled datasets are decoration (e.g. the stock chart's history join).
          filter: (item) => !!item.dataset.label,
        },
        zoom: {
          limits: {
            x: {
              min: dataSpan.min - (dataSpan.max - dataSpan.min) * 0.025,
              max: dataSpan.max + (dataSpan.max - dataSpan.min) * 0.025,
              minRange: minRangeMs,
            },
          },
          pan: {
            enabled: enablePan,
            mode: "x",
            onPanComplete: ({ chart }) => pushIfChanged(chart),
          },
          zoom: {
            wheel: { enabled: false },
            pinch: { enabled: true },
            mode: "x",
            onZoomComplete: ({ chart }) => pushIfChanged(chart),
          },
        },
        ...(todayValue
          ? {
              todayLine: {
                value: todayValue,
                label: t("components.timeseriesChart.today"),
              },
            }
          : {}),
        ...pluginOptions,
      },
    };
  }, [
    viewRange.min,
    viewRange.max,
    yAxisLabel,
    minRangeMs,
    todayValue,
    t,
    formatYTick,
    dataSpan.min,
    dataSpan.max,
    onUserRange,
    enablePan,
    pluginOptions,
  ]);

  // The relative wrapper is the positioned ancestor for any overlay passed via
  // `children` (e.g. demand's totals popup). The popup's own width measurement
  // lives in the demand wrapper, so the core needs no ResizeObserver.
  return (
    <div className="relative w-full" style={{ height }}>
      {isLoading ? (
        skeleton
      ) : (
        <>
          <Line data={data} options={options} plugins={plugins} />
          {children}
        </>
      )}
    </div>
  );
}
